import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null,
  });
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const role = localStorage.getItem('role') || 'student';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load documents',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Auto-hide snackbar after 4 seconds
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        closeSnackbar();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateFile = (file, input = null) => {
    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: 'File size must be less than 10MB',
        severity: 'error'
      });
      if (input) input.value = ''; // Clear the file input
      return false;
    }

    // Check file type
    const allowedTypes = ['pdf', 'doc', 'docx', 'png', 'jpg', 'ppt', 'pptx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: 'Only PDF, DOC, DOCX, PNG, JPG, PPT, and PPTX files are allowed',
        severity: 'error'
      });
      if (input) input.value = ''; // Clear the file input
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && validateFile(selectedFile, e.target)) {
      setFormData(prev => ({
        ...prev,
        file: selectedFile,
      }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFormData(prev => ({
          ...prev,
          file: droppedFile,
        }));
      }
    }
  }; const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setSnackbar({
        open: true,
        message: 'You must be logged in as a teacher to upload a document',
        severity: 'error'
      });
      return;
    }
    if (!formData.title.trim() || !formData.file) {
      setSnackbar({
        open: true,
        message: 'Title and file are required',
        severity: 'warning'
      });
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('file', formData.file);

      await axios.post(`${process.env.REACT_APP_API_URL}/api/documents`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      setSnackbar({
        open: true,
        message: 'Document uploaded successfully!',
        severity: 'success'
      });
      setFormData({ title: '', description: '', file: null });
      setShowUploadForm(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error uploading document',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!token) {
      setSnackbar({
        open: true,
        message: 'You must be logged in as a teacher to delete a document',
        severity: 'error'
      });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({
        open: true,
        message: 'Document deleted successfully!',
        severity: 'success'
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error deleting document',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (filename) => {
    const extension = filename?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M7.75,7A0.75,0.75 0 0,0 7,7.75V16.25A0.75,0.75 0 0,0 7.75,17H9.5A2.5,2.5 0 0,0 12,14.5V9.5A2.5,2.5 0 0,0 9.5,7H7.75M9.5,8.5A1,1 0 0,1 10.5,9.5V14.5A1,1 0 0,1 9.5,15.5H8.5V8.5H9.5M13.5,7V8.5H15.25A0.75,0.75 0 0,1 16,9.25V10.75A0.75,0.75 0 0,1 15.25,11.5H13.5V17H12V7H13.5Z" />
          </svg>
        );
      case 'doc':
      case 'docx':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        );
      case 'xls':
      case 'xlsx':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z" />
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        );
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="documents-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .documents-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .documents-container::before {
          content: '';
          position: absolute;
          top: 8%;
          right: 12%;
          width: 140px;
          height: 140px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          animation: float 4.5s ease-in-out infinite;
        }

        .documents-container::after {
          content: '';
          position: absolute;
          bottom: 18%;
          left: 6%;
          width: 110px;
          height: 110px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.04);
          animation: float 3.2s ease-in-out infinite;
          animation-delay: 2s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-18px) rotate(12deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes docSlide {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pulse-element {
          position: absolute;
          top: 45%;
          right: 20%;
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: pulse 2.8s ease-in-out infinite;
        }

        .documents-wrapper {
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .documents-count {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 500;
        }

        .upload-btn {
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(254, 107, 139, 0.3);
        }

        .upload-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(254, 107, 139, 0.4);
        }

        .form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .form-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #FE6B8B, #FF8E53, #4ECDC4, #667eea);
          border-radius: 20px 20px 0 0;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .form-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .form-input {
          width: 100%;
          padding: 1rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
          resize: vertical;
        }

        .form-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .form-textarea {
          min-height: 100px;
          font-family: inherit;
        }

        .file-upload-area {
          border: 2px dashed rgba(102, 126, 234, 0.3);
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
          background: rgba(102, 126, 234, 0.02);
        }

        .file-upload-area:hover, .file-upload-area.drag-active {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .file-upload-icon {
          width: 48px;
          height: 48px;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          color: #667eea;
        }

        .file-upload-text {
          color: #374151;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        .file-upload-hint {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .selected-file {
          background: rgba(16, 185, 129, 0.1);
          border: 2px solid rgba(16, 185, 129, 0.3);
          padding: 1rem;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #059669;
        }

        .file-icon {
          width: 32px;
          height: 32px;
          background: rgba(16, 185, 129, 0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .file-info {
          flex: 1;
        }

        .file-name {
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .file-size {
          font-size: 0.85rem;
          opacity: 0.8;
        }

        .remove-file {
          background: none;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          transition: background 0.2s ease;
        }

        .remove-file:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .submit-btn {
          flex: 1;
          min-width: 150px;
          padding: 1rem 2rem;
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(254, 107, 139, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .cancel-btn {
          padding: 1rem 2rem;
          background: rgba(107, 114, 128, 0.1);
          color: #374151;
          border: 2px solid rgba(107, 114, 128, 0.2);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: rgba(107, 114, 128, 0.15);
          border-color: rgba(107, 114, 128, 0.3);
        }

        .documents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .document-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 18px;
          padding: 1.8rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          animation: docSlide 0.5s ease-out;
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .document-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .document-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #667eea, #764ba2);
        }

        .doc-header {
          display: flex;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .doc-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-right: 1rem;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .doc-content {
          flex: 1;
        }

        .doc-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.75rem;
        }

        .doc-description {
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          flex-grow: 1;
        }

        .doc-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          margin-top: auto;
          padding-top: 1rem;
          border-top: 1px solid rgba(0,0,0,0.05);
        }

        .download-btn {
          flex: 1;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-decoration: none;
        }

        .download-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
          color: white;
          text-decoration: none;
        }

        .delete-btn {
          width: 40px;
          height: 40px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: translateY(-2px);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
        }

        .loading-content {
          text-align: center;
          color: white;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .section-icon {
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .documents-container {
            padding: 1rem 0.5rem;
          }
          
          .documents-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .form-container {
            padding: 2rem 1.5rem;
          }
          
          .document-card {
            padding: 1.5rem;
          }
          
          .action-bar {
            flex-direction: column;
            align-items: stretch;
          }
          
          .form-actions {
            flex-direction: column;
          }
          
          .submit-btn {
            min-width: unset;
          }

          .doc-actions {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <div className="pulse-element"></div>

      <div className="documents-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Educational Resources
          </div>

          <h1 className="main-title">
            <span className="gradient-text">Document</span> Library
          </h1>

          <p className="subtitle">
            Access syllabus, question papers, and study materials
          </p>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="documents-count">
            {documents.length} {documents.length === 1 ? 'Document' : 'Documents'} Available
          </div>

          {role === 'teacher' && (
            <button
              className="upload-btn"
              onClick={() => setShowUploadForm(!showUploadForm)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
              {showUploadForm ? 'Cancel' : 'Upload Document'}
            </button>
          )}
        </div>

        {/* Upload Form */}
        {role === 'teacher' && showUploadForm && (
          <div className="form-container">
            <div className="form-title">
              <div className="form-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                </svg>
              </div>
              Upload New Document
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Document Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  placeholder="e.g. Mathematics Syllabus 2024, Final Exam Question Paper"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-input form-textarea"
                  placeholder="Add a brief description of this document..."
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Select File *</label>
                {!formData.file ? (
                  <div
                    className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <div className="file-upload-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </div>
                    <div className="file-upload-text">
                      Click to browse or drag and drop files
                    </div>
                    <div className="file-upload-hint">
                      Supports PDF, DOC, DOCX, XLS, XLSX, and image files
                    </div>
                  </div>
                ) : (
                  <div className="selected-file">
                    <div className="file-icon">
                      {getFileIcon(formData.file.name)}
                    </div>
                    <div className="file-info">
                      <div className="file-name">{formData.file.name}</div>
                      <div className="file-size">{formatFileSize(formData.file.size)}</div>
                    </div>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => setFormData(prev => ({ ...prev, file: null }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </button>
                  </div>
                )}
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp,.svg"
                  required
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="loading-spinner" style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white' }}></div>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                    </svg>
                  )}
                  {loading ? 'Uploading...' : 'Upload Document'}
                </button>

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowUploadForm(false);
                    setFormData({ title: '', description: '', file: null });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Documents Display */}
        <div className="section-title">
          <div className="section-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6,2A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V4A2,2 0 0,0 18,2H6M6,4H18V20H6V4M8,6V8H16V6H8M8,10V12H16V10H8M8,14V16H16V14H8Z" />
            </svg>
          </div>
          Available Documents
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <p>Loading documents...</p>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <h3 className="empty-title">No Documents Available</h3>
            <p className="empty-description">
              There are currently no documents in the library. Check back later for updates.
            </p>
          </div>
        ) : (
          <div className="documents-grid">
            {documents.map((doc, index) => (
              <div key={doc._id || doc.id} className="document-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="doc-header">
                  <div className="doc-icon">
                    {getFileIcon(doc.title)}
                  </div>
                  <div className="doc-content">
                    <h3 className="doc-title">{doc.title}</h3>
                  </div>
                </div>

                {doc.description && (
                  <div className="doc-description">
                    {doc.description}
                  </div>
                )}

                <div className="doc-actions">
                  <a
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                    Download
                  </a>

                  {role === 'teacher' && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(doc._id || doc.id)}
                      title="Delete document"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className={`snackbar ${snackbar.severity}`}>
          <span>{snackbar.message}</span>
          <button className="snackbar-close" onClick={closeSnackbar}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};


// Add styles
const style = document.createElement('style');
style.textContent = `
  .snackbar {
    position: fixed;
    bottom: 20px; 
    left: 20px;
    background-color: #333;
    color: white;
    padding: 12px 24px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    z-index: 1000;
    min-width: 300px;
    animation: slideIn 0.3s ease-out;
  }

  .snackbar.success {
    background-color: #4caf50;
  }

  .snackbar.error {
    background-color: #f44336;
  }

  .snackbar.warning {
    background-color: #ff9800;
  }

  .snackbar.info {
    background-color: #2196f3;
  }

  .snackbar-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .snackbar-close:hover {
    background-color: rgba(255,255,255,0.1);
  }

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);

export default Documents;